import Token from '~/api/Token';
import axios from 'axios';
import AuthService from '~/services/AuthService';
import router from '~/router';
import URLS from '~/URLS';

const tokenEndpoint = `${process.env.VUE_APP_IDENTITY_SERVER_URL}token/refresh`;

// This is an axios-interceptor that asks for a new access token if a response
// returns a 401. This requires that there also is an refresh-token available.
const createAxiosResponseInterceptor = () => {
    const interceptor = axios.interceptors.response.use(
        response => response,
        error => {
            const response = error.response;
            if (!response) {
                return Promise.reject(error);
            }

            // Reject promise if usual error
            if (response.status !== 401 && response.status !== 403) {
                return Promise.reject(error);
            }

            if (!AuthService.isLoggedIn()) {
                return Promise.reject(error);
            }

            if (!shouldRefreshToken()) {
                return Promise.reject(error);
            }

            // Make sure axios does not loop if incoming request also returns 401
            axios.interceptors.response.eject(interceptor);

            return getAccessByRefresh()
                .then(data => {
                    // If successfully recieved new token, execute the failed request again with the new token.
                    error.response.config.headers['Authorization'] =
                        'Bearer ' + Token.getAccess();
                    return axios(error.response.config);
                })
                .catch(error => {
                    // If not able to recieve new access token, log the user out.
                    AuthService.logOut();
                    router.push(URLS.findjobs);
                    return rej();
                })
                .finally(createAxiosResponseInterceptor);
        }
    );
};

// If there is not an access token, but there is an refresh token
export const shouldRefreshToken = () => {
    if (Token.getAccess()) {
        return false;
    }

    return Boolean(Token.getRefresh());
};

// Gets an access token from identity-server by existing refresh-token
export const getAccessByRefresh = async () => {
    // Get refresh token
    const refreshToken = Token.getRefresh();
    if (!refreshToken) {
        return Promise.reject();
    }

    return new Promise((res, rej) => {
        // Get new access token with refresh token
        axios({
            method: 'POST',
            url: tokenEndpoint,
            data: {
                refreshToken,
            },
        }).then(data => {
            const response = data.data || {};
            const newAccessToken = response.accessToken;
            const newRefreshToken = response.refreshToken;

            if (newAccessToken) {
                Token.setAccess(newAccessToken);
            }
            if (newRefreshToken) {
                Token.setRefresh(newRefreshToken);
            }
            return res(response);
        });
    });
};

// Initialize axios-refresh-token interceptor
createAxiosResponseInterceptor();

// Get new access-token if there is an available refresh-token
if (shouldRefreshToken()) {
    getAccessByRefresh().then(data => {
        AuthService.loadUserData();
        AuthService.setIsLoggedIn(true);
    });
}
